// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
import { useCallback, useEffect, useRef, useState } from 'react';

import type { QrCodeLayout, SlideUpQrCode } from '#src/app/pages/psf/promoteOrShare/shared/hooks';

/** `grid` is the production share grid; `qr` is psf_promote_or_share_qr_code_experiment's view. */
type SlideUpStep = 'grid' | 'qr';

/**
 * Resets to the grid on every open. The reset runs during render, not in an effect, so an open never
 * flashes the last view and a close keeps the view through the exit animation:
 * https://react.dev/learn/you-might-not-need-an-effect
 */
function useSlideUpStep(isOpen: boolean) {
	const [step, setStep] = useState<SlideUpStep>('grid');
	const [wasOpen, setWasOpen] = useState(isOpen);
	if (wasOpen !== isOpen) {
		setWasOpen(isOpen);
		if (isOpen) setStep('grid');
	}
	return { step, setStep };
}

/**
 * EXPERIMENT: psf_promote_or_share_qr_code_experiment.
 * `qrView` is set only while the QR view shows.
 */
export function useQrCodeSteps(
	isOpen: boolean,
	qrCodeLayout: QrCodeLayout | null | undefined,
	qrCode: SlideUpQrCode | undefined,
) {
	const { step, setStep } = useSlideUpStep(isOpen);
	const qrTileRef = useRef<HTMLDivElement>(null);
	const shouldFocusQrTileRef = useRef(false);

	const openQrView = useCallback(() => setStep('qr'), [setStep]);
	const showGrid = useCallback(() => {
		shouldFocusQrTileRef.current = true;
		setStep('grid');
	}, [setStep]);

	// The back button unmounts with the QR view, so focus returns to the tile.
	useEffect(() => {
		if (step !== 'grid' || !shouldFocusQrTileRef.current) return;
		shouldFocusQrTileRef.current = false;
		qrTileRef.current?.querySelector('button')?.focus();
	}, [step]);
	// No poster view yet, so Print poster is a no-op.
	const openPosterView = useCallback(() => {}, []);

	const qrView = step === 'qr' && qrCodeLayout && qrCode ? { qrCode, showPrint: qrCodeLayout.showPrint } : null;

	return { showQrTile: Boolean(qrCodeLayout && qrCode), qrView, qrTileRef, openQrView, showGrid, openPosterView };
}
